
import {
  computed,
  defineComponent,
  PropType,
  ref,
  onMounted,
  onUnmounted,
} from "vue";
import EditObject from "@/components/EditObject.vue";
import domtoimage from "dom-to-image";
import { EditStore } from "@/stores";
import { frameToResourceUrl, dragDataKeyImageObject } from "@/lib/shared";
import { IImageMaterial, isText } from "@/services/Models";
import { toRelativePos } from "@/lib/shared";
import { consts } from "../services/Models";

export default defineComponent({
  name: "EditArea",
  components: {
    EditObject,
  },
  props: {
    editStore: { type: Object as PropType<EditStore> },
    frameOrder: { type: Number },
  },
  setup(props) {
    const editor = ref<HTMLElement>();
    const sizeParent = ref<HTMLElement>();

    // 配置されているオブジェクトを含む1コマ
    const frameWithObjects = computed(() =>
      props.editStore?.state.frameWithEdit.find(
        // コマ入れ替え機能によりidは重複する場合があるのでorderで検索する
        (x) => x.frame.order == props.frameOrder
      )
    );
    // 編集前のオリジナルサイズ
    const naturalSize = ref(consts.initialSize);
    if (frameWithObjects.value !== undefined) {
      const frameImg = new Image();
      frameImg.onload = () => {
        naturalSize.value = {
          width: frameImg.naturalWidth,
          height: frameImg.naturalHeight,
        };
        updateSize();
      };
      // フレームをURLに変換
      frameImg.src = frameToResourceUrl(
        frameWithObjects.value.frame,
        frameWithObjects.value.edit.isBalloonOn
      );
    }
    const editorSize = ref(consts.initialSize);
    const editorScale = ref(consts.initialScale);
    const findParent: (
      target: HTMLElement | null | undefined,
      f: (elm: HTMLElement) => boolean
    ) => HTMLElement | null = (target, f) => {
      if (target === undefined || target == null) return null;
      if (f(target)) return target;
      return findParent(target.parentElement, f);
    };

    // 画像の配置
    const drop = (e: DragEvent) => {
      if (!e.dataTransfer) return;
      const jsonImageObject = e.dataTransfer.getData(dragDataKeyImageObject);
      if (!jsonImageObject) return;
      const imageObject = JSON.parse(jsonImageObject) as IImageMaterial;
      if (!imageObject.path) return;

      // 座標を計算
      let x = undefined as number | undefined;
      let y = undefined as number | undefined;
      if (editorSize.value && editor.value !== undefined) {
        const p = toRelativePos(editor.value, { x: e.pageX, y: e.pageY });
        x = p.x / editorSize.value.width;
        y = p.y / editorSize.value.height;
      }
      props.editStore?.addImage(imageObject, x, y);
    };
    const dragover = (e: DragEvent) => {
      if (!e.dataTransfer) return;
      if (e.dataTransfer.types.includes(dragDataKeyImageObject)) {
        e.preventDefault();
      }
    };

    onMounted(() => {
      sizeParent.value =
        findParent(
          editor.value,
          (elm) => elm.clientHeight != 0 && elm.clientWidth != 0
        ) ?? undefined;
      window.addEventListener("resize", updateSize);
    });
    onUnmounted(() => window.removeEventListener("resize", updateSize));

    const updateSize = () => {
      if (sizeParent.value == null) return;
      let w = naturalSize.value.width;
      let h = naturalSize.value.height;
      const scaleW = naturalSize.value.width / sizeParent.value.clientWidth;
      const scaleH = naturalSize.value.height / sizeParent.value.clientHeight;
      const scale = Math.max(scaleW, scaleH);
      if (consts.initialScale < scale) {
        w /= scale;
        h /= scale;
      }

      editorSize.value = {
        width: w,
        height: h,
      };
      editorScale.value = 1 / scale;
    };

    // 外部公開用メソッド
    // FIXME こいつもEditStoreに移動したい
    const getImage = async () => {
      if (editor.value === undefined) return;
      const parent = editor.value.parentElement;
      if (parent == null) return;

      // 非表示になってるのでクローンして画像化
      const clone = editor.value.cloneNode(true) as HTMLElement;

      parent.appendChild(clone);
      const style = clone.style;
      style.position = "absolute";
      style.left = "-9999px";
      style.top = "-9999px";
      style.zIndex = "-1";
      style.display = "";

      // オリジナルサイズにする
      style.width = `${naturalSize.value.width}px`;
      style.height = `${naturalSize.value.height}px`;

      // 編集オブジェクト達のscaleもオリジナルサイズに合わせる
      const editElements = Array.from(clone.querySelectorAll(".thumb"));
      const editObjects = frameWithObjects.value?.edit.items ?? [];
      for (let i = 0; i < editElements.length; i++) {
        const editElement = editElements[i];
        const editObject = editObjects[i];
        const objStyle = (editElement as HTMLElement).style;
        // scaleを消す
        objStyle.transform = objStyle.transform.replace(/scale\(.*?\)/i, "");
        // 位置を移動
        objStyle.left = `${editObject.x * naturalSize.value.width}px`;
        objStyle.top = `${editObject.y * naturalSize.value.height}px`;
        // テキストオブジェクトの場合は領域拡張する（意図しない自動改行対策）
        if (isText(editObject)) {
          const textElements = editElement.querySelector(".text-content");
          const objTextStyle = (textElements as HTMLElement).style;
          if (editObject.isVertical) {
            objTextStyle.height = `${editElement.clientHeight + 1}px`;
          } else {
            objTextStyle.width = `${editElement.clientWidth + 1}px`;
          }
        }
      }
      const dataURL = await domtoimage.toSvg(clone, {
        style: {
          position: "",
          left: "0",
          top: "0",
        },
        quality: consts.defaultQuality,
      });
      // // DEBUG
      // var debug = window.open("about:blank");
      // if (debug != null) {
      //   const img = new Image();
      //   img.src = dataURL;
      //   debug.document.body.appendChild(img);
      // }
      parent.removeChild(clone);
      return dataURL;
    };
    const getSelectedElement = () => {
      if (!editor.value) return;
      const selectedElement = editor.value.querySelector(".selected");
      if (!selectedElement) return;
      return selectedElement;
    };
    return {
      editor,
      naturalSize,
      editorSize,
      editorScale,
      frameWithObjects,
      frameToResourceUrl,
      getImage,
      getSelectedElement,
      drop,
      dragover,
    };
  },
});
